/**
 * Allow / deny buckets for the Dashboard's access chart over the header's
 * time range (ZTA design evaluation F26). Hourly up to two days, daily beyond.
 *
 * lib/accessTimeline.ts covers a fixed 24 hours ending now; the Dashboard's
 * window is now the user's, so the buckets follow it. Axis labels are short
 * strings for the chart's category axis; row timestamps use ds Timestamp.
 */
import { format } from 'date-fns';
import type { ResolvedRange } from '@/lib/timeRange';

export interface AccessLogLike {
  created_at?: string | null;
  result?: string | null;
}

export interface AccessBucket {
  key: string;
  label: string;
  start: Date;
  allow: number;
  deny: number;
}

const HOUR = 3_600_000;
const DAY = 24 * HOUR;
/** Daily buckets past this many would make an unreadable axis. */
const MAX_BUCKETS = 120;

export function bucketAccess(
  logs: AccessLogLike[],
  window: ResolvedRange,
  now: Date = new Date(),
): { buckets: AccessBucket[]; unit: 'hour' | 'day' } {
  const end = window.to ? new Date(window.to) : now;
  const startRaw = window.from ? new Date(window.from) : new Date(end.getTime() - DAY);
  const span = Math.max(0, end.getTime() - startRaw.getTime());
  const unit: 'hour' | 'day' = span <= 2 * DAY ? 'hour' : 'day';
  const step = unit === 'hour' ? HOUR : DAY;

  const first = new Date(startRaw);
  if (unit === 'hour') first.setMinutes(0, 0, 0);
  else first.setHours(0, 0, 0, 0);

  const keyOf = (d: Date) => format(d, unit === 'hour' ? 'yyyy-MM-dd HH' : 'yyyy-MM-dd');
  const sameDay = span <= DAY;
  const buckets: AccessBucket[] = [];
  const byKey = new Map<string, AccessBucket>();
  for (let t = first.getTime(); t <= end.getTime() && buckets.length < MAX_BUCKETS; ) {
    const start = new Date(t);
    const key = keyOf(start);
    if (!byKey.has(key)) {
      // Within a day, the date shows on the first bar and at midnight, so a
      // label repeats nowhere and the axis stays short.
      const datedHour = !sameDay || buckets.length === 0 || start.getHours() === 0;
      const label = unit === 'day' ? format(start, 'MMM d') : format(start, datedHour ? 'MMM d HH:00' : 'HH:00');
      const b: AccessBucket = { key, label, start, allow: 0, deny: 0 };
      buckets.push(b);
      byKey.set(key, b);
    }
    // Step by calendar unit so a DST change cannot skip or repeat a bucket.
    const next = new Date(start);
    if (unit === 'hour') next.setHours(next.getHours() + 1);
    else next.setDate(next.getDate() + 1);
    t = next.getTime() > t ? next.getTime() : t + step;
  }

  const from = startRaw.getTime();
  const to = end.getTime();
  for (const log of logs) {
    if (!log.created_at) continue;
    const created = new Date(log.created_at);
    const ms = created.getTime();
    if (Number.isNaN(ms) || ms < from || ms > to) continue;
    const b = byKey.get(keyOf(created));
    if (!b) continue;
    if (log.result === 'allow') b.allow++;
    else b.deny++;
  }
  return { buckets, unit };
}
